if (!customElements.get('ls-delivery-services')) {
  customElements.define('ls-delivery-services', class extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.querySelectorAll('[data-service-checkbox]').forEach((checkbox) => {
        checkbox.disabled = checkbox.dataset.existing === 'true';
        checkbox.addEventListener('change', () => this.updateSummary(), options);
      });
      this.querySelectorAll('[data-add-service]').forEach((button) => {
        button.disabled = false;
        button.addEventListener('click', () => this.addFromCart(button), options);
      });
      this.updateSummary();
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    get selectedServices() {
      return Array.from(this.querySelectorAll('[data-service-checkbox]:checked:not(:disabled)'));
    }

    updateSummary() {
      const summary = this.querySelector('[data-services-summary]');
      if (!summary) return;
      const selected = this.selectedServices;
      const total = selected.reduce((sum, checkbox) => sum + Number(checkbox.dataset.price), 0);
      const price = new Intl.NumberFormat(document.documentElement.lang || 'es', {
        style: 'currency', currency: this.dataset.currency || 'EUR'
      }).format(total / 100);
      summary.textContent = selected.length ? this.dataset.selectedTotal.replace('[price]', price) : this.dataset.optionalHint;
      summary.classList.toggle('is-selected', selected.length > 0);
    }

    async readCart() {
      const response = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart unavailable');
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    }

    async buildAddRequest(formData) {
      const selected = this.selectedServices;
      if (!selected.length) return null;
      const cart = await this.readCart();
      const sofa = { id: formData.get('id'), quantity: Number(formData.get('quantity')) || 1 };
      const properties = {};
      for (const [name, value] of formData.entries()) {
        const property = name.match(/^properties\[(.+)\]$/);
        if (property) {
          if (typeof value !== 'string') throw new Error('File properties require multipart');
          properties[property[1]] = value;
        }
      }
      if (Object.keys(properties).length) sofa.properties = properties;
      if (formData.get('selling_plan')) sofa.selling_plan = formData.get('selling_plan');
      const services = selected
        .filter((checkbox) => !cart.items.some((item) => String(item.product_id) === checkbox.dataset.productId))
        .map((checkbox) => ({ id: checkbox.dataset.variantId, quantity: 1 }));
      if (!services.length) return null;
      const request = { items: [sofa, ...services] };
      if (formData.get('sections')) request.sections = formData.get('sections');
      if (formData.get('sections_url')) request.sections_url = formData.get('sections_url');
      return request;
    }

    async addFromCart(button) {
      if (this.busy) return;
      this.busy = true;
      const buttons = Array.from(this.querySelectorAll('[data-add-service]'));
      const checkouts = Array.from(document.querySelectorAll('[name="checkout"][form="cart"]'));
      const checkoutStates = checkouts.map((control) => control.disabled);
      const error = this.querySelector('[data-services-error]');
      const originalText = button.textContent;
      error.hidden = true;
      buttons.forEach((control) => control.disabled = true);
      checkouts.forEach((control) => control.disabled = true);
      button.textContent = this.dataset.addingLabel;
      this.setAttribute('aria-busy', 'true');
      try {
        const cart = await this.readCart();
        const hasEligibleProduct = cart.items.some((item) =>
          String(item.product_type).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase() === 'sofa' ||
          ['10925038567761', '10925056164177'].includes(String(item.product_id))
        );
        if (hasEligibleProduct && !cart.items.some((item) => String(item.product_id) === button.dataset.productId)) {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: button.dataset.variantId, quantity: 1 }] })
          });
          const result = await response.json();
          if (!response.ok || result.status) {
            const failure = new Error('Service add rejected');
            failure.publicMessage = result.description;
            throw failure;
          }
        }
        // Render the authoritative totals and service state together using the native cart page.
        window.location.reload();
      } catch (exception) {
        error.textContent = exception.publicMessage || this.dataset.errorMessage;
        // A failed response is not retried: the customer can review the cart before another attempt.
        error.hidden = false;
        buttons.forEach((control) => control.disabled = false);
        checkouts.forEach((control, index) => control.disabled = checkoutStates[index]);
        button.textContent = originalText;
        this.busy = false;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
